import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Talk from 'talkjs';
import { AuthService } from '~core/security/auth.service';
import { AuthNgrx } from '~model/ngrx/auth-ngrx';
import { TalkJsApiService } from '~core/api/talk-js-api.service';

@Injectable({ providedIn: 'root' })
export class ChatNotificationsService {
  readonly unreadCount$ = new BehaviorSubject<number>(0);

  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly talkJsApi = inject(TalkJsApiService);
  private session: Talk.Session | null = null;
  private unreadsSubscription: { unsubscribe: () => void } | null = null;
  private initializing = false;

  constructor() {
    this.authService.authNgrx$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(auth => {
        if (this.isAuthenticated(auth)) {
          this.setupSubscription(auth);
        } else {
          this.cleanup();
        }
      });

    this.destroyRef.onDestroy(() => this.cleanup());
  }

  private isAuthenticated(auth: AuthNgrx): boolean {
    return !!auth.empresa && !!auth.nickname && !!auth.token;
  }

  private async setupSubscription(auth: AuthNgrx): Promise<void> {
    if (this.session || this.initializing) return;

    this.initializing = true;
    try {
      // const { appId } = await firstValueFrom(this.talkJsApi.getSession('operator'));
      const appId = 'tzDcwXyb';

      await Talk.ready;

      const userId = `dispatch_${auth.empresa}`;
      const me = new Talk.User({
        id: userId,
        name: auth.nickname!,
        role: 'operator',
        ...(auth.email && { email: auth.email }),
      });

      this.session = new Talk.Session({
        appId,
        me,
        tokenFetcher: () => firstValueFrom(this.talkJsApi.getSession('operator').pipe(map(s => s.token))),
      });

      this.unreadsSubscription = this.session.unreads.onChange(unreadConversations => {
        this.unreadCount$.next(unreadConversations.length);
      });
    } finally {
      this.initializing = false;
    }
  }

  private cleanup(): void {
    this.unreadsSubscription?.unsubscribe();
    this.unreadsSubscription = null;
    this.session?.destroy();
    this.session = null;
    this.unreadCount$.next(0);
  }
}
